import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Heart, CreditCard, CheckCircle2 } from 'lucide-react';

function DonationModal({ isOpen, onClose }) {
  const [frequency, setFrequency] = useState('one-time');
  const [amount, setAmount] = useState(20);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);

  // Lock/unlock body scroll when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Lock body scroll
      document.body.style.overflow = 'hidden';
    } else {
      // Unlock body scroll
      document.body.style.overflow = 'auto';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const frequencyOptions = [
    { value: 'one-time', label: 'One-Time Gift', popular: false },
    { value: 'monthly', label: 'Monthly', popular: true },
    { value: 'quarterly', label: 'Every 3 Months', popular: false },
    { value: 'semi-annual', label: 'Every 6 Months', popular: false },
    { value: 'yearly', label: 'Yearly', popular: false }
  ];

  const amountPresets = [5, 10, 20, 50];

  const stripeLink = 'https://donate.stripe.placeholder';
  const paypalLink = 'https://paypal.placeholder';

  const getFrequencyLabel = () => {
    const option = frequencyOptions.find(opt => opt.value === frequency);
    return option ? option.label.toLowerCase() : 'one-time';
  };

  const getFinalAmount = () => {
    return isCustom ? (parseFloat(customAmount) || 0) : amount;
  };

  const handleAmountClick = (preset) => {
    setAmount(preset);
    setIsCustom(false);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+\.?\d{0,2}$/.test(value)) {
      setCustomAmount(value);
      setIsCustom(true);
    }
  };

  const handleDonate = (provider) => {
    const finalAmount = getFinalAmount();
    if (finalAmount <= 0) {
      return;
    }

    // In production, these links would include amount and frequency parameters
    const link = provider === 'stripe' ? stripeLink : paypalLink;
    window.open(`${link}?amount=${finalAmount}&frequency=${frequency}`, '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="donation-modal">
        {/* Scrollable Modal Body - wraps everything */}
        <div className="donation-modal-body">
          <DialogHeader>
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <Heart 
                style={{ 
                  width: '48px', 
                  height: '48px', 
                  color: '#F0C96A',
                  margin: '0 auto 1rem'
                }} 
              />
            </div>
            <DialogTitle className="donation-title">
              Its Written AI – Support the Mission
            </DialogTitle>
            <DialogDescription className="donation-description">
              Help us bring His Written Word to the world. Your gift keeps Its Written AI free, 
              sends daily devotionals, and develops new features for believers everywhere.
            </DialogDescription>
          </DialogHeader>

          <div className="donation-content donation-content-compact">
          {/* Bible Verse - Always Visible */}
          <div className="donation-verse donation-verse-inline">
            <p className="verse-text verse-text-inline">
              "Each of you should give what you have decided in your heart to give, 
              not reluctantly or under compulsion, for God loves a cheerful giver."
            </p>
            <p className="verse-reference verse-reference-inline">— 2 Corinthians 9:7</p>
          </div>

          {/* Frequency Selector - Compact */}
          <div className="donation-section donation-section-compact">
            <Label className="donation-section-label donation-label-compact">Donation Frequency</Label>
            <div className="frequency-options frequency-options-compact">
              {frequencyOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setFrequency(option.value)}
                  className={`frequency-option frequency-option-compact ${frequency === option.value ? 'active' : ''} ${option.popular ? 'has-badge' : ''}`}
                >
                  {option.popular && (
                    <span className="popular-badge popular-badge-compact">Most Popular</span>
                  )}
                  {frequency === option.value && (
                    <CheckCircle2 className="frequency-check" />
                  )}
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Amount Presets - Compact */}
          <div className="donation-section donation-section-compact">
            <Label className="donation-section-label donation-label-compact">Donation Amount</Label>
            <div className="amount-presets amount-presets-compact">
              {amountPresets.map((preset) => (
                <button
                  key={preset}
                  onClick={() => handleAmountClick(preset)}
                  className={`amount-preset amount-preset-compact ${!isCustom && amount === preset ? 'active' : ''}`}
                >
                  ${preset}
                </button>
              ))}
            </div>

            <div className="custom-amount-container custom-amount-compact">
              <Label htmlFor="custom-amount" className="custom-amount-label custom-label-compact">
                Custom Amount
              </Label>
              <div className="custom-amount-input-wrapper">
                <span className="currency-symbol">$</span>
                <Input
                  id="custom-amount"
                  type="text"
                  placeholder="Enter amount"
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  onFocus={() => setIsCustom(true)}
                  className="custom-amount-input custom-input-compact"
                />
              </div>
            </div>
          </div>

          {/* Donation Summary - Compact */}
          <div className="donation-summary donation-summary-compact">
            <p className="summary-text summary-text-compact">
              You are giving: <span className="summary-amount summary-amount-compact">${getFinalAmount().toFixed(2)}</span>
              {' '}as a <span className="summary-frequency-text">{getFrequencyLabel()}</span> donation.
            </p>
          </div>

          {/* Payment Buttons - Compact */}
          <div className="donation-buttons donation-buttons-compact">
            <Button 
              onClick={() => handleDonate('stripe')}
              className="donate-button donate-button-compact stripe-button"
              disabled={getFinalAmount() <= 0}
            >
              <CreditCard style={{ width: '18px', height: '18px', marginRight: '0.5rem' }} />
              Donate with Stripe
            </Button>

            <Button 
              onClick={() => handleDonate('paypal')}
              variant="outline"
              className="donate-button donate-button-compact paypal-button"
              disabled={getFinalAmount() <= 0}
            >
              <svg 
                style={{ width: '18px', height: '18px', marginRight: '0.5rem' }}
                viewBox="0 0 24 24" 
                fill="currentColor"
              >
                <path d="M8.32 21.97a.546.546 0 0 1-.26-.32c-.03-.15-.01-.3.06-.44l2.38-6.5h3.28c3.09 0 5.37-1.62 5.37-4.82 0-2.62-1.67-4.44-4.18-4.44H8.93c-.38 0-.71.28-.77.66l-2.48 11.4a.79.79 0 0 1-.77.66H1.64c-.42 0-.77-.34-.77-.77 0-.06 0-.12.02-.18L3.47 3.9c.17-1.02 1.05-1.78 2.08-1.78h7.41c4.92 0 7.97 3.16 7.97 7.53 0 5.28-3.68 8.46-9.13 8.46h-2.37l-.82 3.7c-.06.34-.36.59-.71.59H8.32z"/>
              </svg>
              Donate with PayPal
            </Button>
          </div>

          {/* Where Your Gift Goes - Moved Below Buttons with Divider */}
          <div className="gift-divider"></div>
          <div className="purpose-section purpose-section-compact purpose-section-below-buttons">
            <h4 className="purpose-title purpose-title-compact">
              <Heart style={{ width: '16px', height: '16px', marginRight: '0.4rem', color: '#F0C96A' }} />
              Where Your Gift Goes
            </h4>
            <ul className="purpose-list purpose-list-compact">
              <li>Daily devotionals</li>
              <li>Servers & hosting</li>
              <li>App & feature development</li>
              <li>Helping new believers grow</li>
            </ul>
          </div>

          <p className="donation-footer donation-footer-compact">
            Thank you for partnering with us! 🙏
          </p>
        </div>
        {/* End of scrollable modal body */}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DonationModal;
